import React, { useState, useEffect, useRef } from "react";
import { useStore } from "../../stores/store";
import { DraggableKeys } from "../../interfaces/config";
import { BaseDraggable } from "./BaseDraggable";
import Legend from "@arcgis/core/widgets/Legend";

const key = DraggableKeys.LEGEND_DRAGGABLE;
const maxHotSpotIntensity = 50;
const minHotSpotIntensity = 5;

export default function LegendDraggable() {
  const { isActive, toggleActiveItem, mapView, config, primaryLayer, mapLoaded } =
    useStore();
  const legendRef = useRef(null);
  const legendLoaded = useRef(false);
  const [selectedRenderType, setSelectedRenderType] = useState("points");
  const [hotSpotIntensity, setHotSpotIntensity] = useState(30);

  useEffect(() => {
    if (mapView && legendRef.current) {
      if (!legendLoaded.current) {
        new Legend({
          view: mapView,
          container: legendRef.current,
        });
      }
    }
  }, [mapView, legendRef]);

  useEffect(() => {
    if (
      config &&
      config.toolbarOptions &&
      config.toolbarOptions.initiallyVisibleTools &&
      config.toolbarOptions.initiallyVisibleTools[key]
    ) {
      toggleActiveItem(key);
    }
  }, [config]);

  useEffect(() => {
    if (!primaryLayer || !mapLoaded) {
      return;
    }

    //reset feature reduction
    primaryLayer.featureReduction = undefined as any;
    primaryLayer.renderer = undefined as any;
    primaryLayer.labelsVisible = false;
    if (!config?.primaryLayer?.rendererOptions) {
      return;
    }

    const renderTypeOptions =
      config?.primaryLayer.rendererOptions[selectedRenderType];

    if (selectedRenderType === "points") {
      primaryLayer.renderer = renderTypeOptions.renderer as any;
    } else if (selectedRenderType === "clusters") {
      primaryLayer.labelsVisible = true;
      primaryLayer.featureReduction = renderTypeOptions.featureReduction as any;
      primaryLayer.renderer = renderTypeOptions.renderer as any;
    } else if (selectedRenderType === "hotSpots") {
      primaryLayer.renderer = {
        type: "heatmap",
        ...renderTypeOptions.hotSpots,
        radius: hotSpotIntensity,
      };
    }
  }, [selectedRenderType, hotSpotIntensity]);

  const onSelectChange = (e) => {
    setSelectedRenderType(e.target.value);
  };

  return (
    <BaseDraggable
      id={key}
      onClose={() => toggleActiveItem(key)}
      visible={isActive(key)}
      initialPosition="top-right"
      headerText="LEGEND"
    >
      {config?.primaryLayer.rendererOptions && (
        <label className="form-control w-full max-w-xs mb-2">
          <div className="label">
            <span className="label-text">
              {config?.primaryLayer.rendererOptions.label}
            </span>
          </div>
          <select
            className="select select-bordered select-sm"
            onChange={onSelectChange}
          >
            {Object.keys(config?.primaryLayer.rendererOptions.titles).map(
              (key) => (
                <option key={key} value={key}>
                  {config?.primaryLayer.rendererOptions.titles[key]}
                </option>
              )
            )}
          </select>
        </label>
      )}

      {selectedRenderType === "hotSpots" && (
        <>
          <p className="font-bold">Intensity:</p>
          <input
            type="range"
            min={minHotSpotIntensity}
            max={maxHotSpotIntensity - minHotSpotIntensity}
            value={maxHotSpotIntensity - hotSpotIntensity}
            onChange={(e: any) => {
              const val = maxHotSpotIntensity - parseInt(e.target.value);
              if (val >= minHotSpotIntensity) {
                setHotSpotIntensity(val);
              } else {
                setHotSpotIntensity(minHotSpotIntensity);
              }
            }}
            className="slider w-full"
            id="myRange"
          />
        </>
      )}

      <div
        className="w-44 max-h-44 md:w-56 bg-white md:max-h-52"
        ref={legendRef}
      ></div>
    </BaseDraggable>
  );
}
